<script setup lang="ts">
import { useSeo } from '@/composables/useSeo'
import PageLayout from '@/components/layout/PageLayout.vue'
import Card from '@/components/ui/Card.vue'
import Icon from '@/components/icons/Icon.vue'

useSeo({
  title: 'About ClearRand',
  description:
    'Learn why ClearRand was built: free, transparent South African financial calculators with formulas, sources and assumptions shown on every page.',
  path: '/about',
})

const values = [
  {
    icon: 'check' as const,
    title: 'Free to use',
    description: 'No subscriptions, no sign-up walls, and no hidden upsells.',
  },
  {
    icon: 'info' as const,
    title: 'Transparent assumptions',
    description: 'Every calculator shows its source data, formulas and limitations.',
  },
  {
    icon: 'calculator' as const,
    title: 'Built for South Africa',
    description: 'Tax brackets, levies, tariffs and rates are tuned for South African context.',
  },
]
</script>

<template>
  <PageLayout
    title="About"
    description="Free calculators for everyday South African financial decisions."
    :breadcrumbs="[{ label: 'About' }]"
  >
    <div class="section-heading">
      <span class="section-heading__eyebrow">Why us</span>
      <h2 class="section-heading__title">What makes ClearRand different</h2>
    </div>
    <div class="about-grid">
      <Card v-for="value in values" :key="value.title" padding="lg" raised accent="primary" hover>
        <div class="about-card__icon">
          <Icon :name="value.icon" :size="24" />
        </div>
        <h2 class="about-card__title">{{ value.title }}</h2>
        <p class="about-card__desc">{{ value.description }}</p>
      </Card>
    </div>

    <article class="prose">
      <p>
        ClearRand is a collection of free, practical tools for South African residents and small
        businesses. Our goal is to make common calculations — from take-home pay to bond repayments
        — easy to understand and easy to trust.
      </p>
      <h2>What we are not</h2>
      <p>
        We do not give financial, tax, legal, lending, estate-agent, solar-installation or
        investment advice. Every calculator is an estimate based on publicly available data and
        clearly stated assumptions. Always confirm important decisions with a qualified
        professional.
      </p>
      <h2>How we stay transparent</h2>
      <ul>
        <li>Every calculator shows its sources, assumptions and last reviewed date.</li>
        <li>Formulas and data are versioned in the open.</li>
        <li>We do not track or store your inputs.</li>
      </ul>
    </article>
  </PageLayout>
</template>

<style scoped>
.section-heading {
  margin-bottom: var(--space-5);
}

.section-heading__eyebrow {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-primary);
}

.section-heading__title {
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  margin: 0;
}

.about-grid {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: 1fr;
  margin-bottom: var(--space-10);
}

@media (min-width: 640px) {
  .about-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.about-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, rgb(10 92 86 / 0.12), rgb(13 148 136 / 0.08));
  color: var(--color-primary);
  margin-bottom: var(--space-4);
}

.about-card__title {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--color-text);
}

.about-card__desc {
  margin-top: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
}

.prose {
  max-width: 70ch;
  color: var(--color-text);
  line-height: var(--leading-relaxed);
}

.prose h2 {
  margin-top: var(--space-8);
  font-size: var(--text-xl);
  font-weight: var(--font-semibold);
}

.prose p {
  margin-top: var(--space-3);
}

.prose ul {
  margin-top: var(--space-3);
  padding-left: var(--space-5);
}
</style>
